import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useAppUser } from '../hooks/useAppUser';
import { Recipe, CookHistory } from '../types/mealPlan';
import { RecipeDetailsView } from '../components/Recipe/RecipeDetailsView';
import { NotesSkeleton } from '../components/Recipe/NotesSkeleton';
import { IngredientRowsSkeleton } from '../components/Recipe/IngredientRowsSkeleton';
import recipeViewClasses from '../components/Recipe/RecipeDetailsView.module.css';
import { Text, Paper, Stack, Group, Skeleton, Box, Flex, Grid } from '@mantine/core';
import { MobileOptimized } from '../components/Layout/MobileOptimized';
import { usePageTitle } from '../hooks/usePageTitle';
import { buildRecipePath } from '../utils/routeHelpers';
import { getRecipeSeed } from '../utils/recipeSeed';
import { getPageCache, setPageCache } from '../utils/pageCache';
import { isRecipeSummary, SUMMARY_VIEW_PARAM } from '../utils/recipeSummary';

type RecipeDetailsCacheEntry = {
  recipe: Recipe;
  isOwner: boolean;
  owner: { uid: string; displayName?: string; photoURL?: string } | null;
  recipeOwnerUid: string | null;
  ownerRecipeCount: number;
  ownerTasteProfile: { personalityName: string; personalityBlurb: string } | null;
};

export const RecipeDetailsPage = () => {
  const { userId, recipeId, slugWithId } = useParams<{ userId: string; recipeId: string; slugWithId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  // Merged app user for profile-derived display state only — the fetch flow
  // below keys off the stable useAuth user so useAppUser's per-profile-load
  // identity churn can't re-trigger it.
  const { user: appUser } = useAppUser();

  // On a direct page load the server embeds the recipe (#__RECIPE__) so we can
  // render full content on the first paint with no loading skeleton/flash.
  // Absent (client-side navigation) → null, and we fall back to fetching.
  const seededRecipe = useMemo(() => getRecipeSeed(location.pathname), [location.pathname]);

  // Session cache of this route's last-displayed details, so revisits (back
  // navigation in particular) also render fully on the first paint and the
  // fetch below refreshes in the background. Never used for create mode.
  const routeKey = slugWithId || (userId && recipeId ? `${userId}:${recipeId}` : null);
  const detailsCacheKey =
    routeKey && recipeId !== 'new' ? `recipe-details:${routeKey}` : null;
  const cachedDetails = getPageCache<RecipeDetailsCacheEntry>(detailsCacheKey);
  // A recipe passed via navigation state renders on the first paint too (it
  // survives history for back/forward); the mount effect still validates it.
  // From a recipe list it's a summary (no ingredients/instructions): the page
  // previews it with skeleton sections until the full recipe loads.
  const navStateRecipe = (location.state?.recipe as Recipe | undefined) ?? null;
  // The editor seeds its fields once, from the recipe it mounts with, and
  // saves them all back. So edit mode waits on the skeleton for a recipe
  // fetched on this visit: a summary would save empty ingredients/steps, and
  // a session-cached copy can be stale (an earlier edit, another device) and
  // would silently overwrite newer data. The server seed is fresh already.
  const editRequested = new URLSearchParams(location.search).get('edit') === 'true';
  const initialRecipe = seededRecipe ?? cachedDetails?.recipe ?? navStateRecipe;

  const [recipe, setRecipe] = useState<Recipe | null>(initialRecipe);
  const [loading, setLoading] = useState(
    !initialRecipe || (editRequested && !seededRecipe)
  );
  // Route key whose recipe has been fetched fresh on this visit (navigating
  // recipe→recipe keeps this page mounted, so it's per route, not a flag)
  const freshFetchedKeyRef = useRef<string | null>(seededRecipe ? detailsCacheKey : null);
  const [error, setError] = useState<string | null>(null);
  const [isOwner, setIsOwner] = useState<boolean>(cachedDetails?.isOwner ?? true); // Default to true for backward compatibility
  const [owner, setOwner] = useState<{ uid: string; displayName?: string; photoURL?: string } | null>(cachedDetails?.owner ?? null);
  const [recipeOwnerUid, setRecipeOwnerUid] = useState<string | null>(cachedDetails?.recipeOwnerUid ?? null);
  // Own-profile visibility comes off the merged app user — session-cached on
  // remounts, refreshed by useAppUser — instead of a bespoke profile fetch.
  // Only meaningful when the viewer owns the recipe.
  const profilePublic = appUser?.profilePublic ?? false;
  const [ownerRecipeCount, setOwnerRecipeCount] = useState<number>(cachedDetails?.ownerRecipeCount ?? 0);
  const [ownerTasteProfile, setOwnerTasteProfile] = useState<{ personalityName: string; personalityBlurb: string } | null>(cachedDetails?.ownerTasteProfile ?? null);
  const [cookHistory, setCookHistory] = useState<CookHistory | null>(null);

  // Tracks whether a recipe is currently displayed. When we render from the
  // server seed, the background refresh must not flip the page back to the
  // loading skeleton or a transient error — guard those on this ref.
  const recipeRef = useRef<Recipe | null>(recipe);
  useEffect(() => {
    recipeRef.current = recipe;
  }, [recipe]);

  // Keep the browser tab title in sync during client-side navigation. On a
  // full page load the server injects the authoritative <title>/meta/OG/
  // canonical, so this page no longer renders its own head tags (which would
  // duplicate/conflict with the server's). usePageTitle just updates the
  // existing <title> — it doesn't add a second one.
  usePageTitle(
    recipe ? `${recipe.name}${owner?.displayName ? ` - ${owner.displayName}` : ''} | Meals You Love` : ''
  );

  // Check if recipe was passed via state
  const stateRecipe = location.state?.recipe as Recipe | undefined;
  
  // Extract meal plan context for scaled recipes
  const mealPlanId = location.state?.mealPlanId as string | undefined;
  const assignmentId = location.state?.assignmentId as string | undefined;
  
  // Check for edit mode and create mode query parameters
  const searchParams = new URLSearchParams(location.search);
  const startInEditMode = searchParams.get('edit') === 'true';
  const createMode = searchParams.get('create') === 'true';

  useEffect(() => {
    // Wait for auth to finish loading before fetching
    if (authLoading) {
      return;
    }

    // Handle create mode - set up a blank recipe
    if (createMode && recipeId === 'new') {
      const blankRecipe: Recipe = {
        id: 'new',
        name: '',
        description: '',
        prepTimeMinutes: 0,
        cookTimeMinutes: 0,
        totalTimeMinutes: 0,
        servings: 4,
        ingredients: [],
        instructions: [],
        cuisine: '',
        difficulty: 'easy',
        dietaryRestrictions: [],
        tags: [],
        origin: 'user_recipe',
        isSaved: false,
      };
      setRecipe(blankRecipe);
      setLoading(false);
      return;
    }

    // Need either slugWithId or userId+recipeId
    if (!slugWithId && (!recipeId || !userId)) {
      setError('Recipe not found');
      setLoading(false);
      return;
    }

    // Use recipe from state if available, but still fetch owner info
    const effectiveRecipeId = recipeId || stateRecipe?.id;
    if (stateRecipe && effectiveRecipeId && stateRecipe.id === effectiveRecipeId) {
      // Never downgrade a full recipe already on screen (seed or session
      // cache) to a list-item summary
      const current = recipeRef.current;
      if (!current || current.id !== stateRecipe.id || isRecipeSummary(current) || !isRecipeSummary(stateRecipe)) {
        setRecipe(stateRecipe);
      }

      // If it's a scaled recipe (from meal plan), no need to fetch owner info
      const isScaledRecipe = stateRecipe?.originalServings !== undefined;
      if (isScaledRecipe) {
        setLoading(false);
        return;
      }
    }

    // Fetch recipe (and owner info) from API
    const fetchRecipe = async () => {
      try {
        // If we already have a recipe to show (e.g. server-seeded), refresh in
        // the background without showing the loading skeleton. Edit mode
        // keeps the skeleton until this visit's fetch lands (see above) —
        // once per route, so a later refetch can't yank an open editor.
        const awaitingFreshForEdit =
          startInEditMode && freshFetchedKeyRef.current !== detailsCacheKey;
        if (!recipeRef.current || awaitingFreshForEdit) {
          setLoading(true);
        }
        setError(null);

        // Use new public endpoint with optional auth
        const headers: HeadersInit = {};
        if (user) {
          const token = await user.getIdToken();
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Use friendly URL endpoint or legacy endpoint
        const apiUrl = slugWithId
          ? `${process.env.REACT_APP_API_URL}/api/recipe/${slugWithId}`
          : `${process.env.REACT_APP_API_URL}/api/u/${userId}/recipes/${recipeId}`;

        const response = await fetch(apiUrl, {
          headers,
        });

        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('This profile is private');
          }
          throw new Error('Recipe not found');
        }

        const data = await response.json();
        freshFetchedKeyRef.current = detailsCacheKey;
        setRecipe(data.recipe);
        setIsOwner(data.isOwner ?? true); // Default to true if not provided
        const isCatalogRecipe = data.owner?.uid === 'catalog';
        setRecipeOwnerUid(data.owner?.uid || null);
        setOwner(isCatalogRecipe ? null : (data.owner || null));

        let fetchedOwnerRecipeCount = 0;
        let fetchedOwnerTasteProfile: { personalityName: string; personalityBlurb: string } | null = null;

        if (!data.isOwner && data.owner?.uid && !isCatalogRecipe) {
          // Fetch owner's public profile for recipe count and taste personality
          try {
            const profileResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/u/${data.owner.uid}?${SUMMARY_VIEW_PARAM}`);
            if (profileResponse.ok) {
              const profileData = await profileResponse.json();
              fetchedOwnerRecipeCount = profileData.recipeCount ?? 0;
              fetchedOwnerTasteProfile = profileData.tasteProfile ?? null;
              setOwnerRecipeCount(fetchedOwnerRecipeCount);
              setOwnerTasteProfile(fetchedOwnerTasteProfile);
            }
          } catch (error) {
            console.error('Error fetching owner profile:', error);
          }
        }

        // Cache what this route now displays so a revisit renders it on the
        // first paint. Written here (not in a state-mirror effect) because
        // navigating recipe→recipe keeps this component mounted, and the old
        // recipe must never be cached under the new route's key.
        setPageCache(detailsCacheKey, {
          recipe: data.recipe,
          isOwner: data.isOwner ?? true,
          owner: isCatalogRecipe ? null : (data.owner || null),
          recipeOwnerUid: data.owner?.uid || null,
          ownerRecipeCount: fetchedOwnerRecipeCount,
          ownerTasteProfile: fetchedOwnerTasteProfile,
        });
      } catch (error: any) {
        console.error('Error fetching recipe:', error);
        // Don't replace already-displayed (seeded) content with an error on a
        // transient background-refresh failure. A summary preview isn't the
        // full content, and edit mode must not open on unverified data, so
        // those failures are surfaced.
        if (
          !recipeRef.current ||
          isRecipeSummary(recipeRef.current) ||
          (startInEditMode && freshFetchedKeyRef.current !== detailsCacheKey)
        ) {
          setError(error.message || 'Failed to load recipe');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRecipe();
  }, [recipeId, userId, slugWithId, user, authLoading, stateRecipe, createMode, startInEditMode, detailsCacheKey]);

  // Fetch cook history for recipe owner
  const cookHistoryRecipeId = recipe?.id;
  useEffect(() => {
    if (!user || !cookHistoryRecipeId || !isOwner || createMode) {
      setCookHistory(null);
      return;
    }

    const fetchCookHistory = async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/recipes/id/${cookHistoryRecipeId}/cook-history`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          setCookHistory(await response.json());
        }
      } catch (error) {
        console.error('Error fetching cook history:', error);
      }
    };

    fetchCookHistory();
  }, [user, cookHistoryRecipeId, isOwner, createMode]);

  const handleBack = () => {
    // For new recipe creation, go to cookbook
    if (createMode) {
      navigate('/cookbook');
      return;
    }
    
    // Try to go back to the previous page, or fall back to home
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleViewCanonical = () => {
    if (recipe) {
      const ownerId = userId || owner?.uid || user?.uid;
      if (ownerId) {
        navigate(buildRecipePath(recipe, ownerId), {
          replace: false,
          state: { fromScaledRecipe: true }
        });
        setRecipe(null);
        setLoading(true);
      }
    }
  };

  // Leaving the editor drops ?edit=true (replacing the history entry), so a
  // refresh or a back/forward return shows the recipe, not the editor again.
  // A no-op without the param — e.g. create mode, whose save already
  // navigated to the new recipe's URL.
  const handleExitEditMode = () => {
    const params = new URLSearchParams(location.search);
    if (params.get('edit') !== 'true') return;
    params.delete('edit');
    const search = params.toString();
    // Drop the nav-state recipe: it's the pre-edit list item, and the page
    // already shows the saved (or fetched) recipe
    const { recipe: _staleRecipe, ...state } = (location.state ?? {}) as Record<string, unknown>;
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true, state }
    );
  };

  const handleSaveRecipe = async (updatedRecipe: Partial<Recipe>) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const token = await user.getIdToken();
      
      if (createMode && recipeId === 'new') {
        // Creating a new recipe
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/create`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatedRecipe),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to create recipe');
        }

        const data = await response.json();
        setRecipe(data.recipe);
        
        // Navigate to the created recipe's page and replace the current URL
        navigate(buildRecipePath(data.recipe, userId!), { replace: true });
      } else {
        // Updating existing recipe
        const id = recipeId || recipe?.id;
        if (!id) {
          throw new Error('Recipe ID missing');
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/id/${id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatedRecipe),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to save recipe');
        }

        const data = await response.json();
        setRecipe(data.recipe); // Update local state with saved recipe
        // Cache what this route now displays, so a revisit (back/forward,
        // or Edit again from the cookbook) starts from the saved recipe
        // rather than the pre-edit copy
        const cached = getPageCache<RecipeDetailsCacheEntry>(detailsCacheKey);
        setPageCache(detailsCacheKey, {
          recipe: data.recipe,
          isOwner: cached?.isOwner ?? isOwner,
          owner: cached?.owner ?? owner,
          recipeOwnerUid: cached?.recipeOwnerUid ?? recipeOwnerUid,
          ownerRecipeCount: cached?.ownerRecipeCount ?? ownerRecipeCount,
          ownerTasteProfile: cached?.ownerTasteProfile ?? ownerTasteProfile,
        });
      }
    } catch (error: any) {
      console.error('Error saving recipe:', error);
      throw error; // Re-throw so the component can handle it
    }
  };

  if (loading) {
    return (
      <MobileOptimized>
        <Stack gap="lg" pt={4}>
          {/* Header: back button + title + menu. Top-aligned like the real
              header — centering floats the icons against the two-line
              mobile title block */}
          <Group align="flex-start">
            <Skeleton height={34} width={34} radius="md" />
            <Box style={{ flex: 1 }}>
              <Stack gap="sm" mb="xs" hiddenFrom="sm">
                <Skeleton height={24} width="100%" />
                <Skeleton height={24} width="60%" />
              </Stack>
              <Skeleton height={28} width="60%" visibleFrom="sm" />
            </Box>
            <Skeleton height={34} width={34} radius="md" ml="auto" />
          </Group>

          {/* Image + Metadata — mirrors RecipeDetailsView: image stretches to
              match the metadata column height on sm+ (Flex align="stretch"). */}
          <Flex gap="lg" direction={{ base: 'column', sm: 'row' }} align="stretch" mt={-3}>
            {/* Recipe image */}
            <Box
              style={{ flexShrink: 0, borderRadius: 8, overflow: 'hidden' }}
              w={{ base: '100%', sm: 300, lg: 400 }}
              h={{ base: 280, sm: 'auto' }}
            >
              <Skeleton h="100%" mih={200} radius={0} />
            </Box>

            {/* Metadata */}
            <Stack gap={27} mt={8} style={{ flex: 1 }}>
              {/* Description */}
              <Stack gap={6}>
                <Skeleton height={14} width="100%" />
                <Skeleton height={14} width="85%" />
              </Stack>

              {/* Metadata stat grid — same container-query grid as the loaded
                  view (2→5 columns based on the column's own width). */}
              <Box className={recipeViewClasses.statGridContainer}>
                <Box className={recipeViewClasses.statGrid}>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Group key={i} gap="xs" align="flex-start" wrap="nowrap">
                      <Skeleton height={20} width={20} radius="sm" />
                      <Stack gap={4}>
                        <Skeleton height={14} width={60} />
                        <Skeleton height={14} width={40} />
                      </Stack>
                    </Group>
                  ))}
                </Box>
              </Box>

              {/* Action buttons (desktop) */}
              <Group gap="sm" display={{ base: 'none', md: 'flex' }} style={{ width: '100%' }}>
                <Skeleton height={36} radius={28} style={{ flex: 1 }} />
                <Skeleton height={36} radius={28} style={{ flex: 1 }} />
              </Group>
            </Stack>
          </Flex>

          {/* Dietary restrictions */}
          <Stack gap="md">
            <Group gap="xs" wrap="wrap">
              <Skeleton height={20} width={90} radius="xl" />
              <Skeleton height={20} width={100} radius="xl" />
              <Skeleton height={20} width={75} radius="xl" />
            </Group>
            {/* Tags */}
            <Group gap="xs" wrap="wrap">
              <Skeleton height={20} width={85} radius="xl" />
              <Skeleton height={20} width={70} radius="xl" />
              <Skeleton height={20} width={95} radius="xl" />
              <Skeleton height={20} width={80} radius="xl" />
              <Skeleton height={20} width={65} radius="xl" />
            </Group>
            {/* Action buttons (mobile) */}
            <Stack gap="sm" mt="md" display={{ base: 'flex', sm: 'none' }}>
              <Skeleton height={42} radius={28} />
              <Skeleton height={42} radius={28} />
            </Stack>
            {/* Action buttons (tablet) */}
            <Group gap="sm" mt="md" grow display={{ base: 'none', sm: 'flex', md: 'none' }}>
              <Skeleton height={42} radius={28} />
              <Skeleton height={42} radius={28} />
            </Group>
          </Stack>

          {/* Ingredients + Instructions grid */}
          <Grid mt={3}>
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Stack gap="md">
                <Skeleton height={24} width={100} />
                <Paper p={{ base: "lg", sm: "md" }} radius="lg" withBorder>
                  <Stack gap={0}>
                    <IngredientRowsSkeleton />
                  </Stack>
                </Paper>
              </Stack>
              {/* Notes (wide screens: under the ingredients, as in the view) */}
              <Box display={{ base: 'none', md: 'block' }} mt="lg">
                <NotesSkeleton />
              </Box>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Stack gap="md">
                <Skeleton height={24} width={110} />
                {Array.from({ length: 6 }).map((_, i) => (
                  <Paper key={i} p={{ base: "lg", sm: "md" }} radius="lg" withBorder>
                    <Stack gap={28}>
                      <Group gap="md" align="flex-start">
                        <Skeleton height={32} width={32} circle />
                        <Stack gap="xs" mt={4} style={{ flex: 1 }}>
                          <Skeleton height={14} width="100%" />
                          <Skeleton height={14} width="95%" />
                          <Skeleton height={14} width={`${45 + (i % 3) * 15}%`} />
                        </Stack>
                      </Group>
                      <Group gap="xs" pl={48}>
                        <Skeleton height={18} width={70} radius="xl" />
                        <Skeleton height={18} width={55} radius="xl" />
                        {i % 2 === 0 && <Skeleton height={18} width={80} radius="xl" />}
                      </Group>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Grid.Col>
            {/* Notes (narrow screens: after the steps, as in the view) */}
            <Grid.Col span={12} display={{ base: 'block', md: 'none' }}>
              <NotesSkeleton />
            </Grid.Col>
          </Grid>
        </Stack>
      </MobileOptimized>
    );
  }

  if (error || !recipe) {
    return (
      <Paper p="xl" radius="lg" withBorder>
        <Stack gap="md" align="center">
          <Text fw={500}>Recipe not found</Text>
          <Text c="dimmed" size="sm">
            The recipe you're looking for doesn't exist or couldn't be loaded.
          </Text>
        </Stack>
      </Paper>
    );
  }

  // Only show canonical link if this is a scaled recipe (has originalServings)
  const isScaledRecipe = recipe.originalServings && recipe.originalServings !== recipe.servings;
  // A list-item summary previewing while the full recipe loads (never in
  // create mode, whose blank recipe has empty arrays)
  const isPreview = isRecipeSummary(recipe);
  // The view tree (rating modal, feedback tags, ingredient/step sections)
  // assumes a full recipe's arrays; a preview gets empty ones, and its
  // sections render skeletons via contentPending until the remount
  const viewRecipe = isPreview
    ? { ...recipe, ingredients: recipe.ingredients ?? [], instructions: recipe.instructions ?? [] }
    : recipe;

  // Head tags (title/description/OG/canonical) are injected server-side on full
  // page loads — see meals-you-love og.ts — so this page renders none of its own
  // (the tab title is kept current via usePageTitle above).
  return (
    <RecipeDetailsView
      // Remount when the full recipe replaces a summary preview, so the
      // view's mount-time state (edit buffers, notes, rating) seeds from it
      key={isPreview ? 'preview' : 'full'}
      recipe={viewRecipe}
      contentPending={isPreview}
      onBack={handleBack}
      onViewCanonical={isScaledRecipe ? handleViewCanonical : undefined}
      onSaveRecipe={handleSaveRecipe}
      onExitEditMode={handleExitEditMode}
      canEdit={!isPreview && (!isScaledRecipe || createMode)} // Canonical recipes or new recipes can be edited
      startInEditMode={!isPreview && (startInEditMode || createMode) && isOwner} // Only allow edit mode if owner
      isOwner={isOwner}
      ownerId={userId || recipeOwnerUid || owner?.uid}
      owner={owner}
      profilePublic={profilePublic}
      ownerRecipeCount={ownerRecipeCount}
      ownerTasteProfile={ownerTasteProfile}
      mealPlanId={mealPlanId}
      assignmentId={assignmentId}
      cookHistory={cookHistory}
    />
  );
};
